/**
 * micro:bit 놀이터 저학년(MB-01-SE) 장면 — 보여 주기 중심(PICNIC-MB-FIELD-001 v0.2 §4.1 · 통로 ① 화면 읽기 · ② 입력).
 *
 * 순수 상태(DOM · 시계 없음 — 지난 시간 ms 와 그때의 보드 모습). 같은 입력이면 같은 결과다.
 *  - 인사 광장(LE01): 웃는 얼굴을 보여 주는 동안 친구가 1초에 한 명씩 와서 인사한다 — 3명.
 *  - 그림책 무대(LE02): 아이콘이 0.5초 넘게 서 있으면 다음 장면 — 바로 앞과 다른 아이콘 3개로 이야기 끝까지.
 *  - 등대 항구(LE03): 화면이 꺼질 때마다 한 번 깜빡 — 1초 안에 다시 깜빡이면 배가 다가오고 3번에 한 척이 들어온다.
 *  - 표정 문지기(LE04): 문지기의 요청(웃어 봐 · 슬퍼 봐) 5번 — A · B 로 그 얼굴을 보여 주면 맞힌 것이다.
 * 흔들면 주사위(LE05)는 lib/games 의 주사위(stepDice · 판 15칸)를 같이 쓴다.
 */
import type { MbFrame } from "./live";
import { iconOf, isBlank } from "./live";
import { bitsOfLeds } from "./font";

// ── 인사 광장(LE01) ──────────────────────────────────────────────────

export const GREET_MS = 1000;
export const FRIENDS_GOAL = 3;
/** 별 셋까지 계속 센다(3D 무대 별 둘 · 셋 — lib/sceneStars) */
export const FRIENDS_MAX = 30;

export interface Greet { readonly happyMs: number; readonly friends: number }
export const initialGreet = (): Greet => ({ happyMs: 0, friends: 0 });

export function stepGreet(s: Greet, dtMs: number, frame: MbFrame | null): Greet {
  if (s.friends >= FRIENDS_MAX || !frame || iconOf(frame.leds) !== "Happy") return s;
  const happyMs = s.happyMs + dtMs;
  return { happyMs, friends: Math.min(FRIENDS_MAX, Math.floor(happyMs / GREET_MS)) };
}

// ── 그림책 무대(LE02) ────────────────────────────────────────────────

export const PAGE_HOLD_MS = 500;
export const PAGES_GOAL = 3;
export const PAGES_MAX = 20;

export interface Story {
  readonly cand: string | null;
  readonly held: number;
  /** 넘긴 장면마다 보여 준 아이콘 */
  readonly pages: readonly string[];
}
export const initialStory = (): Story => ({ cand: null, held: 0, pages: [] });

export function stepStory(s: Story, dtMs: number, frame: MbFrame | null): Story {
  if (s.pages.length >= PAGES_MAX) return s;
  const icon = frame ? iconOf(frame.leds) : null;
  if (!icon) return s.cand === null ? s : { ...s, cand: null, held: 0 };
  if (icon !== s.cand) return { ...s, cand: icon, held: 0 };
  const held = s.held + dtMs;
  const turn = held >= PAGE_HOLD_MS && s.pages[s.pages.length - 1] !== icon;
  return { cand: icon, held, pages: turn ? [...s.pages, icon] : s.pages };
}

// ── 등대 항구(LE03) ──────────────────────────────────────────────────

export const BLINK_FAST_MS = 1000;
export const BLINKS_PER_SHIP = 3;
export const SHIPS_GOAL = 3;
export const SHIPS_MAX = 30;

export interface Harbor {
  readonly clock: number;
  readonly wasLit: boolean;
  /** 마지막으로 꺼진 시각(놀이 시계) */
  readonly lastOff: number | null;
  /** 최근 깜빡임 간격(ms) */
  readonly interval: number | null;
  /** 빠른 깜빡임 수(다음 배까지) */
  readonly fast: number;
  readonly ships: number;
}
export const initialHarbor = (): Harbor => ({ clock: 0, wasLit: false, lastOff: null, interval: null, fast: 0, ships: 0 });

export function stepHarbor(s: Harbor, dtMs: number, frame: MbFrame | null): Harbor {
  const clock = s.clock + dtMs;
  if (!frame || s.ships >= SHIPS_MAX) return { ...s, clock };
  const lit = !isBlank(frame.leds);
  if (!(s.wasLit && !lit)) return { ...s, clock, wasLit: lit };
  // 켜짐 → 꺼짐 = 한 번 깜빡
  const interval = s.lastOff === null ? null : clock - s.lastOff;
  let { fast, ships } = s;
  if (interval !== null && interval <= BLINK_FAST_MS) {
    fast += 1;
    if (fast >= BLINKS_PER_SHIP) { ships += 1; fast = 0; }
  }
  return { clock, wasLit: lit, lastOff: clock, interval, fast, ships };
}

// ── 표정 문지기(LE04) ────────────────────────────────────────────────

/** 문지기의 요청 차례(난수 없음) */
export const GATE_ASKS: readonly ("Happy" | "Sad")[] = ["Happy", "Sad", "Happy", "Happy", "Sad"];

export interface Gate {
  readonly i: number;
  /** 마지막으로 보여 준 얼굴이 요청과 달랐다(다시 기다림) */
  readonly other: string | null;
  readonly prev: string | null;
  readonly wasA: boolean;
  readonly wasB: boolean;
}
export const initialGate = (): Gate => ({ i: 0, other: null, prev: null, wasA: false, wasB: false });

/** 얼굴이 새로 뜨거나(화면이 바뀜) A · B 를 다시 눌렀을 때 한 번 본다 — 같은 얼굴을 두 번 요청해도 버튼을 다시 누르면 된다 */
export function stepGate(s: Gate, frame: MbFrame | null): Gate {
  if (!frame || s.i >= GATE_ASKS.length) return s;
  const bits = bitsOfLeds(frame.leds);
  const icon = iconOf(frame.leds);
  const fresh = (icon === "Happy" || icon === "Sad") && (bits !== s.prev || (frame.a && !s.wasA) || (frame.b && !s.wasB));
  const base = { ...s, prev: bits, wasA: frame.a, wasB: frame.b };
  if (!fresh) return base;
  return icon === GATE_ASKS[s.i] ? { ...base, i: s.i + 1, other: null } : { ...base, other: icon };
}
